import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import { addToast } from "@/store/app/action.js";
import { getProductTags, deleteProductTag } from "@/api/productTags.js";
import ProductTagModal, { TagScanConfirmModal } from "./ProductTagModal.jsx";
import TagScanJobsModal from "./TagScanJobsModal.jsx";

function ProductTagsTable() {
  const dispatch = useDispatch();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [scanConfirm, setScanConfirm] = useState(null); // { tagId, reverify }
  const [jobsOpen, setJobsOpen] = useState(false);
  const [jobsTagFilter, setJobsTagFilter] = useState("");

  const loadTags = async () => {
    try {
      setLoading(true);
      const data = await getProductTags();
      setTags(Array.isArray(data) ? data : []);
    } catch (error) {
      dispatch(addToast({
        message: error.response?.data?.message || "Failed to load product tags",
        type: "error",
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTags();
  }, []);

  const handleCreateNew = () => {
    setSelectedTag(null);
    setModalOpen(true);
  };

  const handleEdit = (tag) => {
    setSelectedTag(tag);
    setModalOpen(true);
  };

  const handleDelete = async (tag) => {
    if (!window.confirm(`Are you sure you want to delete "${tag.id}"?`)) return;
    try {
      setDeleting(tag.id);
      await deleteProductTag(tag.id);
      dispatch(addToast({ message: "Product tag deleted", type: "success" }));
      loadTags();
    } catch (error) {
      dispatch(addToast({
        message: error.response?.data?.message || "Failed to delete product tag",
        type: "error",
      }));
    } finally {
      setDeleting(null);
    }
  };

  const openJobs = (tagId = "") => {
    setJobsTagFilter(tagId || "");
    setJobsOpen(true);
  };

  const handleRequestScan = ({ tagId, reverify }) => {
    setScanConfirm({ tagId, reverify: Boolean(reverify) });
  };

  const filteredTags = useMemo(() => {
    if (!searchQuery.trim()) return tags;
    const q = searchQuery.toLowerCase();
    return tags.filter(
      (t) =>
        t.id?.toLowerCase().includes(q) ||
        t.display?.value?.toLowerCase().includes(q)
    );
  }, [tags, searchQuery]);

  return (
    <div>
      <div className="sm:flex sm:justify-between sm:items-center mb-4 gap-4">
        <input
          type="text"
          placeholder="Search by ID or display value..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="form-input w-full sm:w-96 mb-3 sm:mb-0"
        />
        <div className="flex items-center gap-2 shrink-0">
          <SecondaryButton onClick={() => openJobs()}>Scan jobs</SecondaryButton>
          <PrimaryButton onClick={handleCreateNew}>Add Tag</PrimaryButton>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="table-auto w-full dark:text-slate-300">
          {loading ? (
            <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
              <tr>
                <td colSpan="5" className="px-2 first:pl-5 last:pr-5 py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-600 dark:text-slate-400">Loading product tags...</p>
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <>
              <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Tag ID</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Display Value</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Ordinal</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Public</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left"></div>
                  </th>
                </tr>
              </thead>
              <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                {filteredTags.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-2 first:pl-5 last:pr-5 py-8 text-center text-slate-500">
                      No product tags found. Create one to get started.
                    </td>
                  </tr>
                ) : (
                  filteredTags.map((tag) => (
                    <tr key={tag.id}>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="font-medium text-slate-800 dark:text-slate-100">{tag.id}</div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        {tag.display?.value ? (
                          tag.display?.color ? (
                            <span
                              className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full"
                              style={{ backgroundColor: tag.display.color, color: "#fff" }}
                            >
                              {tag.display.value}
                            </span>
                          ) : (
                            <div className="text-left">{tag.display.value}</div>
                          )
                        ) : (
                          <div className="text-left">—</div>
                        )}
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-left">
                          {tag.display?.ordinal !== undefined && tag.display?.ordinal !== null
                            ? tag.display.ordinal
                            : "—"}
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full ${
                          tag.public
                            ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-400"
                            : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
                        }`}>
                          {tag.public ? "Public" : "Private"}
                        </span>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {tag.product_matching_prompt ? (
                            <button
                              type="button"
                              onClick={() =>
                                setScanConfirm({ tagId: tag.id, reverify: true })
                              }
                              className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300"
                            >
                              Scan
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => openJobs(tag.id)}
                            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                          >
                            Jobs
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEdit(tag)}
                            className="text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(tag)}
                            disabled={deleting === tag.id}
                            className="text-rose-500 hover:text-rose-600 dark:text-rose-400 dark:hover:text-rose-300"
                          >
                            {deleting === tag.id ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </>
          )}
        </table>
      </div>

      <ProductTagModal
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        tag={selectedTag}
        onSave={loadTags}
        onRequestScan={handleRequestScan}
      />

      <TagScanConfirmModal
        open={Boolean(scanConfirm)}
        tagId={scanConfirm?.tagId}
        reverify={Boolean(scanConfirm?.reverify)}
        onClose={() => setScanConfirm(null)}
        onQueued={() => openJobs(scanConfirm?.tagId)}
      />

      <TagScanJobsModal
        open={jobsOpen}
        setOpen={setJobsOpen}
        initialTagId={jobsTagFilter}
      />
    </div>
  );
}

export default ProductTagsTable;
